// CustomerTypeManagementView.tsx
import React, { useState } from 'react';
import { Table, Button, Modal, Form, Input, Select, message } from 'antd';
import { useTheme } from '@/components/common/ThemeProvider';
import {useGetAllProducts, useGetProducts} from "@/hooks/customer/useCustomer";
import {CustomerTypeDTO} from '@/shared/api/model';
import {
    useCreateCustomerType, useDeleteCustomerType,
    useGetAllCustomerTypes,
    useUpdateCustomerType
} from "@/modules/hooks/useCustomerCategory";

const { Option } = Select;

const CustomerTypeManagementView: React.FC = () => {
    const [form] = Form.useForm();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingCustomerType, setEditingCustomerType] = useState<CustomerTypeDTO | null>(null);
    const { dark } = useTheme();

    // Correctly use the hooks based on their actual return types
    const { data: customerTypes, isLoading } = useGetAllCustomerTypes();
    const [{ data: products }] = useGetAllProducts();
    const createCustomerType = useCreateCustomerType();
    const updateCustomerType = useUpdateCustomerType();
    const deleteCustomerType = useDeleteCustomerType();

    const showModal = (customerType?: CustomerTypeDTO) => {
        setEditingCustomerType(customerType || null);
        
        // When editing, extract product IDs from the products array
        if (customerType) {
            const formValues = {
                ...customerType,
                // @ts-ignore
                productIds: customerType.products?.map(product => product.glNo) || []
            };
            form.setFieldsValue(formValues);
        } else {
            form.resetFields();
        }
        
        setIsModalVisible(true);
    };

    const handleOk = () => {
        if(!products) {
            message.error("Products not loaded. Please try again!");
            return;
        }
        form.validateFields().then(values => {
            // Map selected product IDs back to product objects
            // @ts-ignore
            const selectedProducts = values.productIds?.map(glNo => 
                products.find(product => product.glNo === glNo)
            ).filter(Boolean) || [];
            
            const customerType: CustomerTypeDTO = {
                ...values,
                code: editingCustomerType?.code || values.code,
                products: selectedProducts
            };
            
            // Remove productIds as it's not part of CustomerTypeDTO
            delete (customerType as any).productIds;
            
            const promise = editingCustomerType
                // @ts-ignore
                ? updateCustomerType.mutateAsync(customerType)
                // @ts-ignore
                : createCustomerType.mutateAsync(customerType);
                
            promise
                .then(() => {
                    setIsModalVisible(false);
                    form.resetFields();
                    message.success(`Customer type ${editingCustomerType ? 'updated' : 'created'} successfully`);
                })
                .catch(error => {
                    console.error('Error:', error);
                    message.error(`Failed to ${editingCustomerType ? 'update' : 'create'} customer type`);
                });
        });
    };

    const handleCancel = () => {
        setIsModalVisible(false);
        form.resetFields();
    };

    const handleDelete = (code: string) => {
        Modal.confirm({
            title: 'Are you sure you want to delete this customer type?',
            content: 'This action cannot be undone.',
            okText: 'Yes',
            okType: 'danger',
            cancelText: 'No',
            onOk() {
                deleteCustomerType.mutateAsync(code)
                    .then(() => {
                        message.success('Customer type deleted successfully');
                    })
                    .catch(error => {
                        console.error('Error:', error);
                        message.error('Failed to delete customer type');
                    });
            },
        });
    };

    const columns = [
        {
            title: 'Code',
            dataIndex: 'code',
            key: 'code',
        },
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (text: string, record: CustomerTypeDTO) => (
                <>
                    <Button type="link" onClick={() => showModal(record)}>Edit</Button>
                    <Button type="link" danger onClick={
                        // @ts-ignore
                        () => handleDelete(record?.code)}>Delete</Button>
                </>
            ),
        },
    ];

    return (
        <div className="p-4">
            <div className="flex justify-between mb-4">
                <h1 className="text-2xl font-bold">Customer Types</h1>
                <Button type="primary" onClick={() => showModal()}>Add Customer Type</Button>
            </div>
            
            <Table
                /* @ts-ignore */
                dataSource={customerTypes} 
                columns={columns} 
                rowKey="code" 
                loading={isLoading}
                className={dark ? 'dark-mode-table' : ''}
            />
            
            <Modal
                title={editingCustomerType ? 'Edit Customer Type' : 'Add Customer Type'}
                open={isModalVisible}
                onOk={handleOk}
                onCancel={handleCancel}
                confirmLoading={createCustomerType.isPending || updateCustomerType.isPending}
            >
                <Form form={form} layout="vertical">
                    <Form.Item
                        name="code"
                        label="Code"
                        rules={[{ required: true, message: 'Please enter the code' }]}
                        hidden={!!editingCustomerType}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        name="name"
                        label="Name"
                        rules={[{ required: true, message: 'Please enter the name' }]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        name="description"
                        label="Description"
                    >
                        <Input.TextArea rows={4} />
                    </Form.Item>
                    <Form.Item
                        name="productIds"
                        label="Associated Products"
                    >
                        <Select mode="multiple" placeholder="Select products">
                            {products?.map(product => (
                                <Option key={product.glNo} value={product.glNo}>
                                    {product.productName}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default CustomerTypeManagementView;